<script setup>
import { onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { usePipelinesStore } from 'dashboard/stores/pipelines';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import PipelinesEmptyState from 'dashboard/components-next/Crm/EmptyState/PipelinesEmptyState.vue';
import LeadIntentsDialog from './LeadIntentsDialog.vue';

const store = usePipelinesStore();
const router = useRouter();
const { t } = useI18n();

const intentsDialog = ref(null);
const newDialog = ref(null);
const deleteDialogRef = ref(null);
const selectedPipeline = ref(null);
const selectedTemplate = ref('revisoes_pendentes');
const customName = ref('');
const creating = ref(false);

const templates = [
  {
    key: 'revisoes_pendentes',
    label: 'Revisões Pendentes',
    icon: 'i-lucide-calendar-clock',
    description: 'Revisões programadas por km/tempo, com recall',
  },
  {
    key: 'leads_instagram',
    label: 'Leads Instagram',
    icon: 'i-lucide-instagram',
    description: 'Captação e qualificação de leads do Instagram',
  },
  {
    key: 'pos_venda_exemplo',
    label: 'Pós-Venda Exemplo',
    icon: 'i-lucide-heart-handshake',
    description: 'Da entrega à fidelização em 12 meses',
  },
  {
    key: 'financiamento',
    label: 'Financiamento',
    icon: 'i-lucide-banknote',
    description: 'Esteira de crédito até aprovação do banco',
  },
  {
    key: 'vendas',
    label: 'Vendas',
    icon: 'i-lucide-trending-up',
    description: '4 stages, follow-up agressivo e consultivo',
  },
  {
    key: 'manutencao',
    label: 'Manutenção',
    icon: 'i-lucide-wrench',
    description: '3 stages + recall pós-atendimento',
  },
  {
    key: 'pos_venda',
    label: 'Pós-venda',
    icon: 'i-lucide-heart-handshake',
    description: 'Acompanhamento de 90 dias',
  },
  {
    key: 'blank',
    label: 'Em branco',
    icon: 'i-lucide-square',
    description: 'Sem stages predefinidos',
  },
];

onMounted(() => store.fetchAll());

function openNew() {
  newDialog.value?.open();
}
function closeDlg() {
  newDialog.value?.close();
}

async function confirmCreate() {
  if (creating.value) return;
  creating.value = true;
  try {
    const pipeline = await store.createFromTemplate(
      selectedTemplate.value,
      customName.value || null
    );
    closeDlg();
    customName.value = '';
    selectedTemplate.value = 'revisoes_pendentes';
    useAlert('Pipeline criado.');
    router.push({
      name: 'settings_pipeline_edit',
      params: { pipelineId: pipeline.id },
    });
  } catch (e) {
    useAlert(e?.response?.data?.error || 'Falha ao criar pipeline.');
  } finally {
    creating.value = false;
  }
}

function onClickEdit(pipeline) {
  router.push({
    name: 'settings_pipeline_edit',
    params: { pipelineId: pipeline.id },
  });
}

function onClickDelete(pipeline) {
  // Confirmação dupla - pipelines costumam ter histórico e exclusão é
  // irreversível. Backend retorna 422 quando há leads abertos.
  selectedPipeline.value = pipeline;
  deleteDialogRef.value?.open();
}

async function confirmDelete() {
  const pipeline = selectedPipeline.value;
  if (!pipeline) return;
  try {
    await store.destroy(pipeline.id);
    useAlert('Pipeline excluído.');
  } catch (e) {
    useAlert(
      e?.response?.data?.error ||
        e?.response?.data?.errors?.join?.(', ') ||
        'Falha ao excluir.'
    );
  } finally {
    deleteDialogRef.value?.close();
    selectedPipeline.value = null;
  }
}
</script>

<template>
  <SettingsLayout
    :is-loading="store.loading"
    loading-message="Carregando pipelines..."
  >
    <template #header>
      <BaseSettingsHeader
        :title="t('PIPELINES.HEADER')"
        :description="t('PIPELINES.DESCRIPTION')"
        feature-name="pipelines"
      >
        <template v-if="store.list.length" #count>
          <span class="text-body-main text-n-slate-11"
            >{{ store.list.length }} pipeline(s)</span
          >
        </template>
        <template #actions>
          <NextButton
            faded
            slate
            icon="i-lucide-target"
            size="sm"
            :label="t('LEAD_INTENTS.MANAGE')"
            @click="intentsDialog?.open()"
          />
          <NextButton
            icon="i-lucide-plus"
            size="sm"
            :label="t('PIPELINES.NEW')"
            @click="openNew"
          />
        </template>
      </BaseSettingsHeader>
    </template>

    <!-- Empty state precisa ocupar a área inteira do body do SettingsLayout
         pra que o backdrop+gradient do EmptyStateLayout renderize correto. -->
    <template v-if="!store.list.length" #body>
      <div class="h-[28rem]">
        <PipelinesEmptyState>
          <template #actions>
            <NextButton
              icon="i-lucide-plus"
              :label="t('PIPELINES.FROM_TEMPLATE')"
              @click="openNew"
            />
          </template>
        </PipelinesEmptyState>
      </div>
    </template>

    <template v-else #body>
      <div class="flex flex-col gap-2 pt-2 pb-10">
        <ol class="flex flex-col gap-2">
          <li
            v-for="pipeline in store.list"
            :key="pipeline.id"
            class="rounded-xl border border-n-weak hover:border-n-slate-7 transition-colors px-4 py-3 flex items-center gap-4"
          >
            <span
              class="size-10 rounded-full flex items-center justify-center flex-shrink-0"
              :style="{ background: `${pipeline.color}1f` }"
            >
              <i
                class="i-lucide-kanban size-5"
                :style="{ color: pipeline.color }"
              />
            </span>

            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-2 min-w-0">
                <p class="text-heading-3 text-n-slate-12 truncate mb-0">
                  {{ pipeline.name }}
                </p>
                <span
                  v-if="!pipeline.active"
                  class="inline-flex items-center px-1.5 py-0.5 rounded bg-n-alpha-2 text-label-small font-medium text-n-slate-11 uppercase tracking-wide"
                  >inativo</span
                >
                <span
                  v-if="pipeline.template_key"
                  class="inline-flex items-center px-1.5 py-0.5 rounded bg-n-brand/10 text-label-small font-medium text-n-brand uppercase tracking-wide"
                  >{{ pipeline.template_key }}</span
                >
              </div>
              <p
                v-if="pipeline.description"
                class="text-body-small text-n-slate-11 mt-0.5 mb-0 line-clamp-1"
              >
                {{ pipeline.description }}
              </p>
              <div
                class="flex items-center gap-4 mt-1 text-body-small text-n-slate-10"
              >
                <span class="inline-flex items-center gap-1">
                  <i class="i-lucide-layers size-3" />
                  {{ t('PIPELINES.STAGES_COUNT', { count: pipeline.stages?.length || 0 }) }}
                </span>
              </div>
            </div>

            <div class="flex items-center gap-2 flex-shrink-0">
              <NextButton
                faded
                slate
                size="sm"
                icon="i-lucide-pen-square"
                label="Editar"
                @click="onClickEdit(pipeline)"
              />
              <NextButton
                faded
                ruby
                size="sm"
                icon="i-lucide-trash-2"
                v-tooltip="'Excluir pipeline'"
                @click="onClickDelete(pipeline)"
              />
            </div>
          </li>
        </ol>
      </div>
    </template>

    <Dialog
      ref="newDialog"
      width="2xl"
      :show-cancel-button="false"
      :show-confirm-button="false"
    >
      <div class="flex flex-col gap-4">
        <div>
          <h3 class="text-heading-3 text-n-slate-12 mb-1">
            Criar pipeline a partir de template
          </h3>
          <p class="text-body-small text-n-slate-11">
            Cada template inclui stages, intents, motivos de perda e templates
            de follow-up.
          </p>
        </div>

        <div class="grid grid-cols-2 gap-2">
          <button
            v-for="tpl in templates"
            :key="tpl.key"
            type="button"
            class="text-left p-3 rounded-xl border-2 transition flex items-start gap-3"
            :class="
              selectedTemplate === tpl.key
                ? 'border-n-brand bg-n-brand/5'
                : 'border-n-weak hover:border-n-slate-7'
            "
            @click="selectedTemplate = tpl.key"
          >
            <div
              class="size-9 rounded-full bg-n-alpha-2 flex items-center justify-center flex-shrink-0"
            >
              <i :class="tpl.icon" class="size-4 text-n-slate-12" />
            </div>
            <div class="min-w-0">
              <div class="text-body-main font-semibold text-n-slate-12">
                {{ tpl.label }}
              </div>
              <p class="text-label-small text-n-slate-11 mt-0.5 mb-0">
                {{ tpl.description }}
              </p>
            </div>
          </button>
        </div>

        <Input
          id="pipeline-name"
          v-model="customName"
          label="Nome (opcional - usa o do template se vazio)"
          placeholder="Ex: Vendas Premium"
        />

        <div class="flex justify-end gap-2 pt-2">
          <NextButton faded slate label="Cancelar" @click="closeDlg" />
          <NextButton
            :is-loading="creating"
            label="Criar pipeline"
            @click="confirmCreate"
          />
        </div>
      </div>
    </Dialog>

    <Dialog
      ref="deleteDialogRef"
      type="alert"
      title="Excluir pipeline"
      :description="`Excluir o pipeline “${selectedPipeline?.name}”? Esta ação não pode ser desfeita. Pipelines com leads abertos não podem ser excluídos - mova os leads antes.`"
      confirm-button-label="Excluir"
      cancel-button-label="Cancelar"
      @confirm="confirmDelete"
    />

    <LeadIntentsDialog ref="intentsDialog" />
  </SettingsLayout>
</template>
